import { consume } from '@lit/context';
import { LitElement, css, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';

import { gameApiContext } from '../../../context';
import type { GameAPIType } from '../../../GameAPI';
import { ANNOUNCEMENT_FADE_OUT_MS, announcementFadeTiming, announcementStyles } from '../../../theme/announcement.css';
import { tokens } from '../../../theme/tokens.css';
import { uiState as uiStateProxy, type UIState } from '../../../UIState';
import { bindValtio } from '../../../ValtioController';

/**
 * CTF Match Result - Victory/Defeat notice overlay
 *
 * Displays when a CTF match ends:
 * - StarMash's victory or defeat banner, picked by your own team
 * - Bounty reward and the winning team underneath
 * - Auto-dismisses after the server's duration (7s from ab-server), fading out
 *
 * The banner PNGs are warmed at boot by AssetWarmup, so the first result
 * never waits on the network.
 *
 * @element ctf-match-result
 */
@customElement('ctf-match-result')
export class CtfMatchResult extends LitElement {
  static override styles = [
    tokens,
    announcementStyles,
    css`
      .result-image {
        /* Fit within the viewport on BOTH axes, preserving the intrinsic
           500×436 ratio (auto width+height = no distortion when the height
           cap binds). The explicit aspect-ratio keeps the box at its final
           size BEFORE the PNG decodes — an unloaded img with auto sizing has
           no intrinsic dimensions, and the notice briefly rendered mis-sized
           and off-center, snapping once the image arrived (rare: cold HTTP
           cache only; AssetWarmup's boot preload covers that case too). */
        display: block;
        width: auto;
        height: auto;
        aspect-ratio: 500 / 436;
        max-width: min(400px, 90vw);
        max-height: calc(100vh - 40px);
        max-height: calc(100dvh - 40px);
        filter: drop-shadow(0 0 20px rgba(0, 0, 0, 0.8));
      }

      .overlay {
        /* Overlaid on the banner, not flowed below it: the VICTORY!/DEFEAT!
           glyphs end ~53% down the image and the lower half is smoke fading
           to transparency, so stacking under the image box floated the
           bounty ~200px from the text. 57% lands it just under the glyphs
           (matching StarMash's tight grouping) and scales with the banner. */
        position: absolute;
        top: 57%;
        left: 50%;
        transform: translateX(-50%);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 6px;
      }

      .bounty {
        white-space: nowrap;
        font-size: var(--font-size-xxl);
        font-weight: bold;
        /* Vanilla's #custom-msg bounty cream (#dedebf) — identical to our
           heading token. (StarMash restyles it white, 16px, normal weight.) */
        color: var(--color-text-heading);
        text-shadow: 0 0 10px rgba(0, 0, 0, 0.9);
      }

      /* Winning-team pill, vanilla's #custom-msg .winner .player: dark pill,
         24px bold, deep team hues (#286aef / #be2829, which StarMash inherits
         while restyling the pill as glowing text on no background). The hues
         are deliberately NOT --color-team-blue/red, which are lighter text
         tints; these sit on the pill at display size. */
      .winner-team {
        white-space: nowrap;
        font-size: 24px;
        font-weight: bold;
        line-height: 38px;
        padding: 5px 14px 4px;
        border-radius: var(--radius-chip);
        background-color: var(--color-bg-chip);
      }

      .winner-team.blue {
        color: #286aef;
      }

      .winner-team.red {
        color: #be2829;
      }
    `,
  ];

  private uiState = bindValtio(this, uiStateProxy);

  @consume({ context: gameApiContext })
  private gameApi!: GameAPIType;

  private dismissTimer: number | null = null;
  private lastResult: UIState['ctfMatchResult'] = null;

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.clearDismissTimer();
  }

  private clearDismissTimer() {
    if (this.dismissTimer !== null) {
      window.clearTimeout(this.dismissTimer);
      this.dismissTimer = null;
    }
  }

  override updated() {
    const result = this.uiState.ctfMatchResult;

    if (result === this.lastResult) return;
    this.lastResult = result;

    this.clearDismissTimer();

    // No zero-duration exemption: the fade is timed from the same number, so a
    // result that never dismissed would sit there faded out. Legacy dismissed a
    // zero at once too.
    if (result) {
      this.dismissTimer = window.setTimeout(() => {
        this.gameApi.dismissCtfMatchResult();
        this.dismissTimer = null;
        this.lastResult = null;
      }, result.duration * 1000);
    }
  }

  override render() {
    const result = this.uiState.ctfMatchResult;

    // Don't render if no result state (null = hidden)
    if (!result) {
      return nothing;
    }

    const imageUrl = result.isVictory ? '/vanilla/ui/gameplay/ctfvictory.png' : '/vanilla/ui/gameplay/ctfdefeat.png';

    return html`
      <div class="result" style=${styleMap(announcementFadeTiming(result.duration * 1000, ANNOUNCEMENT_FADE_OUT_MS))}>
        <img
          class="result-image"
          width="500"
          height="436"
          src="${imageUrl}"
          alt="${result.isVictory ? 'Victory' : 'Defeat'}"
        />
        <div class="overlay">
          <div class="bounty">+${result.bounty} BOUNTY</div>
          <div class="winner-team ${result.winnerTeam === 1 ? 'blue' : 'red'}">
            ${result.winnerTeam === 1 ? 'BLUE TEAM' : 'RED TEAM'}
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ctf-match-result': CtfMatchResult;
  }
}
